import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import API from '../../utils/api';
import toast from 'react-hot-toast';

const STATUSES = [
  'placed',
  'confirmed',
  'processing',
  'shipped',
  'out_for_delivery',
  'delivered',
  'cancelled',
];

export default function AdminOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOrder = async () => {
    try {
      const { data } = await API.get(`/orders/${id}`);
      setOrder(data);
    } catch {
      toast.error('Order not found');
      navigate('/admin/orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
  }, [id]);

  const updateStatus = async (status) => {
    try {
      await API.put(`/orders/${id}/status`, { status });
      toast.success('Order status updated');
      loadOrder();
    } catch {
      toast.error('Status update failed');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Order Details">
        <div className="spinner" />
      </AdminLayout>
    );
  }

  if (!order) return null;

  const customerName = order.user?.name || order.guestName || order.address?.fullName || 'Guest';
  const customerPhone = order.user?.phone || order.guestPhone || order.address?.phone || '—';
  const customerEmail = order.user?.email || order.guestEmail || '—';

  return (
    <AdminLayout title={`Order #${order.orderId}`}>
      <button
        onClick={() => navigate('/admin/orders')}
        style={{
          marginBottom: 20,
          padding: '8px 14px',
          border: '1px solid var(--border)',
          background: 'white',
          cursor: 'pointer',
        }}
      >
        ← Back to Orders
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={card}>
          <h3 style={title}>Customer Details</h3>
          <p><b>Name:</b> {customerName}</p>
          <p><b>Phone:</b> {customerPhone}</p>
          <p><b>Email:</b> {customerEmail}</p>
        </div>

        <div style={card}>
          <h3 style={title}>Payment Details</h3>
          <p><b>Method:</b> {order.paymentMethod}</p>
          <p><b>Payment Status:</b> {order.paymentStatus}</p>
          <p><b>Order Status:</b> {order.orderStatus}</p>
          <p><b>Payment ID:</b> {order.razorpayPaymentId || '—'}</p>
        </div>

        <div style={card}>
          <h3 style={title}>Delivery Address</h3>
          <p>{order.address?.fullName}</p>
          <p>{order.address?.phone}</p>
          <p>{order.address?.line1}</p>
          <p>{order.address?.line2}</p>
          <p>{order.address?.city}, {order.address?.state}</p>
          <p>{order.address?.pincode}</p>
        </div>

        <div style={card}>
          <h3 style={title}>Update Status</h3>
          <select
            value={order.orderStatus}
            onChange={(e) => updateStatus(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              border: '1px solid var(--border)',
              background: 'var(--ivory)',
            }}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ ...card, marginTop: 20 }}>
        <h3 style={title}>Products Ordered</h3>

        {order.items?.map((item, index) => (
          <div
            key={index}
            style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr auto',
              gap: 14,
              alignItems: 'center',
              padding: '14px 0',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <img
              src={item.image || '/images/jewelry-placeholder.jpg'}
              alt={item.name}
              style={{
                width: 60,
                height: 60,
                objectFit: 'cover',
                borderRadius: 6,
                background: 'var(--cream)',
              }}
            />

            <div>
              <div style={{ fontWeight: 600 }}>{item.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                Category: {item.category || '—'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                Qty: {item.qty} × ₹{item.price?.toLocaleString()}
              </div>
            </div>

            <div style={{ fontWeight: 600, color: 'var(--maroon)' }}>
              ₹{(item.price * item.qty).toLocaleString()}
            </div>
          </div>
        ))}

        <div style={{ marginTop: 20, textAlign: 'right', lineHeight: 2 }}>
          <div>Subtotal: ₹{order.subtotal?.toLocaleString()}</div>
          <div>Shipping: ₹{order.shippingCharge?.toLocaleString()}</div>
          <h3 style={{ color: 'var(--maroon)' }}>
            Total: ₹{order.totalAmount?.toLocaleString()}
          </h3>
        </div>
      </div>

      <style>{`
        @media(max-width: 768px) {
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </AdminLayout>
  );
}

const card = {
  background: 'white',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 20,
};

const title = {
  fontFamily: "'Cormorant Garamond',serif",
  fontSize: 22,
  marginBottom: 14,
  color: 'var(--maroon)',
};